import React from 'react';
import { graphql, useStaticQuery, Link } from 'gatsby';
import { ThemeProvider } from 'styled-components';
import { themes } from '../../ui/design-tokens';
import OImage from '../OImage';

import Wysiwyg from '../Wysiwyg';
import VideoContainer from '../../ui/VideoContainer';
import { ButtonSmall } from '../../ui/Button';
import { getSrcset, externalLink } from '../../ui/helpers';
import Wrapper from '../../ui/Wrappers';
import Play from '-!svg-react-loader!../../ui/svgs/play-button.svg';
import { Module, Top, Bottom, Img, ResponsiveVideo } from './styles';

interface IProps {
  theme: string;
  heading?: string;
  title?: string;
  description?: string;
  videoUrl?: string;
  videoType?: string;
  imageUrl?: any;
  imageAlt?: string;
  cta?: { uri: string; title: string };
  gtmAction?: string;
  centered?: boolean;
  forceCenter?: boolean;
}

const HomeFullVideo = ({
  theme,
  heading,
  title,
  description,
  videoUrl,
  videoType,
  imageUrl,
  imageAlt,
  cta,
  gtmAction,
  centered,
  forceCenter
}: IProps) => {
  const cleanedTheme = theme || 'white';

  const data = useStaticQuery(graphql`
    query {
      site {
        siteMetadata {
          drupalUrl
        }
      }
    }
  `);

  const drupalUrl = data?.site?.siteMetadata?.drupalUrl;

  // Función para convertir URL de YouTube a URL de embed
  const getYouTubeEmbedUrl = (url: string) => {
    if (!url) return '';
    let videoId = '';
    
    try {
      // Manejar diferentes formatos de URL de YouTube
      if (url.includes('youtu.be/')) {
        videoId = url.split('youtu.be/')[1].split('?')[0];
      } else if (url.includes('youtube.com/watch')) {
        const urlParams = new URLSearchParams(new URL(url).search);
        videoId = urlParams.get('v') || '';
      }
      
      return videoId ? `https://www.youtube-nocookie.com/embed/${videoId}` : '';
    } catch (error) {
      console.error('Error parsing YouTube URL:', error);
      return '';
    }
  };

  return (
    <ThemeProvider theme={themes.basic[cleanedTheme]}>
      <Module centered={centered} forceCenter={forceCenter}>
        {(title || description) && (
          <Wrapper fullwidth={true} size="reader" as={Top} centered={centered} forceCenter={forceCenter}>
            {title &&
              (heading === 'heading_2' ? (
                <h2 className="title">{title}</h2>
              ) : (
                <h3 className="title">{title}</h3>
              ))}
            {description && (
              <Wysiwyg className="description" text={description} theme={cleanedTheme} />
            )}

            {/* Renderizar video de YouTube o video normal según el tipo */}
            {videoUrl && (
              <div className="video-container">
                {videoType === 'youtube' ? (
                  <div className="video-container youtube-container" style={{ marginTop: '1rem' }}>
                    <iframe
                      src={getYouTubeEmbedUrl(videoUrl)}
                      title={title || 'YouTube video'}
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
               
                    />
                  </div>
                ) : (
                  <ResponsiveVideo controls width="740">
                    <source src={videoUrl} type={videoType || 'video/mp4'} />
                    Tu navegador no soporta el tag de video.
                  </ResponsiveVideo>
                )}
              </div>
            )}

            {!videoUrl && cta && cta?.uri && (
              <div className="ctaContainer">
                <ButtonSmall
                  as={externalLink(cta.uri) ? 'a' : Link}
                  className="btn"
                  href={externalLink(cta.uri) ? cta.uri : null}
                  to={externalLink(cta.uri) ? null : cta.uri}
                  target={externalLink(cta.uri) ? '_blank' : undefined}
                  rel={externalLink(cta.uri) ? 'nofollow noopener noreferrer' : undefined}
                  secondary={1}
                  data-gtm-event
                  data-gtm-category="cta"
                  data-gtm-action={gtmAction}
                  data-gtm-label={`${cta.title}_${cta.uri}`}
                >
                  {cta.title}
                </ButtonSmall>
              </div>
            )}
          </Wrapper>
        )}

        {imageUrl?.original?.href && (
          <Wrapper fullwidth={true} size="mainwrapper" as={Img}>
            <OImage
              src={imageUrl.original.href}
              aspectRatio={
                Number(imageUrl.original.meta?.width) > 0 && Number(imageUrl.original.meta?.height) > 0
                  ? Number(imageUrl.original.meta.width) / Number(imageUrl.original.meta.height)
                  : 1.5
              }
              srcSet={getSrcset(imageUrl)}
              webp={getSrcset(imageUrl, true)}
              alt={imageAlt || ''}
              className="image"
            />
          </Wrapper>
        )}
      </Module>
    </ThemeProvider>
  );
};

export default HomeFullVideo;
